import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Product {
  id: string;
  name: string;
  category: string;
  diameter: number;
  wallThickness: number;
  pressure: string;
  standard: string;
  material: string;
  description: string;
  applications: string[];
  price: number;
  unit: string;
  image: string;
  inStock: boolean;
}

export interface CartItem {
  product: Product;
  quantity: number;
  length?: number; // для труб - длина в метрах
  notes?: string;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  totalItems: number;
  totalAmount: number;
}

const initialState: CartState = {
  items: [],
  isOpen: false,
  totalItems: 0,
  totalAmount: 0,
};

const calculateTotals = (items: CartItem[]) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = items.reduce((sum, item) => {
    const itemLength = item.length || 1;
    return sum + (item.product.price * item.quantity * itemLength);
  }, 0);
  
  return { totalItems, totalAmount };
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<{ product: Product; quantity: number; length?: number; notes?: string }>) => {
      const { product, quantity, length, notes } = action.payload;
      const existingItem = state.items.find(item => 
        item.product.id === product.id && 
        item.length === length
      );

      if (existingItem) {
        existingItem.quantity += quantity;
        if (notes) {
          existingItem.notes = notes;
        }
      } else {
        state.items.push({
          product,
          quantity,
          length,
          notes,
        });
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalAmount = totals.totalAmount;
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.product.id !== action.payload);
      
      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalAmount = totals.totalAmount;
    },

    updateQuantity: (state, action: PayloadAction<{ productId: string; quantity: number; length?: number }>) => {
      const { productId, quantity, length } = action.payload;
      const item = state.items.find(item => 
        item.product.id === productId && 
        item.length === length
      );
      
      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(i => i !== item);
        } else {
          item.quantity = quantity;
        }
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalAmount = totals.totalAmount;
    },

    updateLength: (state, action: PayloadAction<{ productId: string; length: number; oldLength?: number }>) => {
      const { productId, length, oldLength } = action.payload;
      const item = state.items.find(item => 
        item.product.id === productId && 
        item.length === oldLength
      );
      
      if (item) {
        item.length = length;
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalAmount = totals.totalAmount;
    },

    updateNotes: (state, action: PayloadAction<{ productId: string; notes: string; length?: number }>) => {
      const { productId, notes, length } = action.payload;
      const item = state.items.find(item => 
        item.product.id === productId && 
        item.length === length
      );
      
      if (item) {
        item.notes = notes;
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalAmount = 0;
    },

    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },

    openCart: (state) => {
      state.isOpen = true;
    },

    closeCart: (state) => {
      state.isOpen = false;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  updateLength,
  updateNotes,
  clearCart,
  toggleCart,
  openCart,
  closeCart,
} = cartSlice.actions;

export default cartSlice.reducer;